import React from 'react';
import Joi from 'joi';
import {Select, RadioGroup} from 'components/forms/fields';
import Section from 'components/forms/Section';
import useFieldData from 'components/forms/hooks/useFieldData';
import {Typography} from '@material-ui/core';
import codFormFields from 'components/forms/cod/fields';

const deckPositions = {
    Master: 'Master',
    'Chief Mate / 1st Mate': 'Chief Mate / 1st Mate',
    '2nd Mate': '2nd Mate',
    '3rd Mate': '3rd Mate',
    Mate: 'Mate',
    'Master of Towing': 'Master of Towing',
    'Mate Pilot of Towing': 'Mate Pilot of Towing',
    'Apprentice Mate (Steersman)': 'Apprentice Mate (Steersman)',
    'Mate (Trainee)': 'Mate (Trainee)',
    'First Class Pilot': 'First Class Pilot',
    'Apprentice Pilot': 'Apprentice Pilot',
    'OUPV Operator': 'OUPV Operator',
    'Able Seaman': 'Able Seaman',
    Boatswain: 'Boatswain',
    Quartermaster: 'Quartermaster',
    'Deck Hand': 'Deck Hand',
    'Crew Member': 'Crew Member',
    'Entry Level': 'Entry Level',
};

const enginePositions = {
    'Chief Engineer': 'Chief Engineer',
    '1st Assistant Engineer': '1st Assistant Engineer',
    '2nd Assistant Engineer': '2nd Assistant Engineer',
    '3rd Assistant Engineer': '3rd Assistant Engineer',
    'Assistant Engineer': 'Assistant Engineer',
    'Designated Duty Engineer': 'Designated Duty Engineer',
    Engineer: 'Engineer',
    'Junior Unlicensed Engineer': 'Junior Unlicensed Engineer',
    'Junior Engineer': 'Junior Engineer',
    'Qualified Member of the Engine Department (QMED)':
        'Qualified Member of the Engine Department (QMED)',
    'Electrician-Refrigerating Engineer': 'Electrician-Refrigerating Engineer',
    Oiler: 'Oiler',
    'Fireman-Watertender': 'Fireman-Watertender',
    'Machinist-Pumpman': 'Machinist-Pumpman',
    Wiper: 'Wiper',
    'Crew Member': 'Crew Member',
    'Entry Level': 'Entry Level',
};

const marinePositions = {
    Master: 'Master',
    'Chief Mate / 1st Mate': 'Chief Mate / 1st Mate',
    '2nd Mate': '2nd Mate',
    '3rd Mate': '3rd Mate',
    Mate: 'Mate',
    'Able Seaman': 'Able Seaman',
    Boatswain: 'Boatswain',
    Quartermaster: 'Quartermaster',
    'Deck Hand': 'Deck Hand',
    'Ballast Control Operator': 'Ballast Control Operator',
    'Ballast Control Operator trainee': 'Ballast Control Operator trainee',
    'Barge Supervisor': 'Barge Supervisor',
    'Barge Supervisor trainee': 'Barge Supervisor trainee',
    'Offshore Manager trainee': 'Offshore Manager trainee',
    'Crew Member': 'Crew Member',
    'Entry Level': 'Entry Level',
};

const maintenancePositions = {
    'Chief Engineer': 'Chief Engineer',
    '1st Assistant Engineer': '1st Assistant Engineer',
    '2nd Assistant Engineer': '2nd Assistant Engineer',
    '3rd Assistant Engineer': '3rd Assistant Engineer',
    'Assistant Engineer': 'Assistant Engineer',
    'Designated Duty Engineer': 'Designated Duty Engineer',
    Engineer: 'Engineer',
    'Junior Unlicensed Engineer': 'Junior Unlicensed Engineer',
    'Junior Engineer': 'Junior Engineer',
    QMED: 'QMED',
    'Electrician-Refrigerating Engineer': 'Electrician-Refrigerating Engineer',
    Oiler: 'Oiler',
    'Fireman-Watertender': 'Fireman-Watertender',
    'Machinist-Pumpman': 'Machinist-Pumpman',
    Wiper: 'Wiper',
    'Ballast Control Operator': 'Ballast Control Operator',
    'Ballast Control Operator trainee': 'Ballast Control Operator trainee',
    'Barge Supervisor': 'Barge Supervisor',
    'Barge Supervisor trainee': 'Barge Supervisor trainee',
    'Offshore Manager trainee': 'Offshore Manager trainee',
    'Crew Member': 'Crew Member',
    'Entry Level': 'Entry Level',
};

const drillingPositions = {
    Driller: 'Driller',
    'Assistant driller': 'Assistant driller',
    'Tool pusher': 'Tool pusher',
    'Assistant tool pusher': 'Assistant tool pusher',
    Mechanic: 'Mechanic',
    'Mechanical supervisor': 'Mechanical supervisor',
    Electrician: 'Electrician',
    'Crane operator': 'Crane operator',
    'Sub-sea specialist': 'Sub-sea specialist',
    'Ballast Control Operator': 'Ballast Control Operator',
    'Barge supervisor': 'Barge supervisor',
    'Barge Supervisor trainee': 'Barge Supervisor trainee',
    'Ballast Control Operator trainee': 'Ballast Control Operator trainee',
    'Crew Member': 'Crew Member',
    'Entry Level': 'Entry Level',
};

const staffPositions = {
    'Crew Member': 'Crew Member',
};

const ratingToPositionName = rating => {
    switch (rating) {
        case 'Marine':
            return codFormFields.offshore_installation_deck_position_served.name;
        case 'Maintenance':
            return codFormFields.offshore_installation_engine_position_served.name;
        case 'Drilling':
            return codFormFields.offshore_installation_drilling_position_served.name;
        case 'Other':
            return codFormFields.offshore_installation_supportstaff_position_served
                .name;
        case 'Engine':
            return codFormFields.engine_position_served.name;
        case 'Deck':
            return codFormFields.deck_position_served.name;
        default:
            return codFormFields.offshore_installation_supportstaff_position_served
                .name;
    }
};

const offshoreSchema = {
    offshore_installation_department: Joi.string().max(20).required(),
    offshore_installation_deck_position_served: Joi.string()
        .max(100)
        .when(Joi.ref('offshore_installation_department'), {
            is: 'Marine',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    offshore_installation_engine_position_served: Joi.string()
        .max(100)
        .when(Joi.ref('offshore_installation_department'), {
            is: 'Maintenance',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    offshore_installation_drilling_position_served: Joi.string()
        .max(100)
        .when(Joi.ref('offshore_installation_department'), {
            is: 'Drilling',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    offshore_installation_supportstaff_position_served: Joi.string()
        .max(100)
        .when(Joi.ref('offshore_installation_department'), {
            is: 'Other',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
};

const typicalSchema = {
    department: Joi.string().max(20).required(),
    engine_position_served: Joi.string()
        .max(100)
        .when(Joi.ref('department'), {
            is: 'Engine',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    deck_position_served: Joi.string()
        .max(100)
        .when(Joi.ref('department'), {
            is: 'Deck',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
};

const initialValues = {
    offshore_installation_department: '',
    offshore_installation_deck_position_served: '',
    offshore_installation_engine_position_served: '',
    offshore_installation_drilling_position_served: '',
    offshore_installation_supportstaff_position_served: '',
    engine_position_served: '',
    deck_position_served: '',
};

const RatingFactory = isOffshore => {
    const Rating = props => {
        const [department, setDepartment] = useFieldData(
            isOffshore
                ? codFormFields.offshore_installation_department.name
                : codFormFields.department.name
        );
        const updateDepartment = e => {
            // Getting data from radio, probably should extract this pattern into a HOC
            setDepartment(e.currentTarget.value);
        };

        return (
            <Section
                {...props}
                {...{
                    schema: isOffshore ? offshoreSchema : typicalSchema,
                    initialValues,
                }}
            >
                <Typography variant='h6' align='left'>
                    Part 5: Rating
                </Typography>
                <Typography variant='body1'>
                    Please select the department and your Rating from the options
                    below. If the rating written on your form is not one of the
                    available selections, please select the closest option and write
                    in your rating below.
                </Typography>
                {isOffshore ? (
                    <RadioGroup
                        name={codFormFields.offshore_installation_department.name}
                        onChange={updateDepartment}
                        label={codFormFields.offshore_installation_department.label}
                        required
                        options={{
                            Marine: 'Marine Department',
                            Maintenance: 'Maintenance Department',
                            Drilling: 'Drilling Department',
                            Other: 'Other Department',
                        }}
                    />
                ) : (
                    <RadioGroup
                        name={codFormFields.department.name}
                        onChange={updateDepartment}
                        label={codFormFields.department.label}
                        required
                        options={{
                            Deck: 'Deck Department',
                            Engine: 'Engine Department',
                        }}
                    />
                )}
                <Select
                    required
                    name={ratingToPositionName(department)}
                    label={
                        department
                            ? `${department} Positions`
                            : 'Please select a department'
                    }
                    options={
                        department === 'Marine'
                            ? marinePositions
                            : department === 'Maintenance'
                            ? maintenancePositions
                            : department === 'Drilling'
                            ? drillingPositions
                            : department === 'Other'
                            ? staffPositions
                            : department === 'Engine'
                            ? enginePositions
                            : department === 'Deck'
                            ? deckPositions
                            : {empty: 'Please select a department'}
                    }
                />

                {props.children}
            </Section>
        );
    };
    Rating.displayName = `Rating${isOffshore && 'Offshore'}`;

    return Rating;
};

export default RatingFactory;
